import React, { useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { Button } from '../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { cn, formatCurrency } from '../../lib/utils';

const AddOnsManager = () => {
  const {
    addOns, addAddOn, updateAddOn, deleteAddOn,
    addOnCategories, addAddOnCategory, updateAddOnCategory, deleteAddOnCategory,
    eventTypes
  } = useStore();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('addons');
  const [isCatAdding, setIsCatAdding] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 50,
    type: 'flat',
    active: true,
    eventTypeIds: [],
    categoryId: '',
    imageUrl: '',
    sortOrder: 0,
  });

  const [catFormData, setCatFormData] = useState({
    name: '',
    description: '',
    selectionMode: 'single',
    required: false,
    sortOrder: 0,
    eventTypeIds: [],
    active: true,
  });

  const handleEdit = (addon) => {
    setEditingId(addon.id);
    setFormData({
      ...addon,
      eventTypeIds: addon.eventTypeIds || [],
      categoryId: addon.categoryId || '',
      imageUrl: addon.imageUrl || '',
      sortOrder: addon.sortOrder || 0,
    });
    setIsAdding(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        categoryId: formData.categoryId || null,
        imageUrl: formData.imageUrl || null,
      };
      if (editingId) {
        await updateAddOn(editingId, dataToSave);
      } else {
        await addAddOn(dataToSave);
      }
      setIsAdding(false);
      setEditingId(null);
      setFormData({ name: '', description: '', price: 50, type: 'flat', active: true, eventTypeIds: [], categoryId: '', imageUrl: '', sortOrder: 0 });
    } catch (err) {
      alert(err.message || 'Failed to save add-on.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this add-on?')) {
      try { await deleteAddOn(id); } catch (err) { alert(err.message); }
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', description: '', price: 50, type: 'flat', active: true, eventTypeIds: [], categoryId: '', imageUrl: '', sortOrder: 0 });
  };

  const handleEditCat = (cat) => {
    setEditingCatId(cat.id);
    setCatFormData({ ...cat });
    setIsCatAdding(true);
  };

  const handleSaveCat = async () => {
    if (!catFormData.name) { alert('Category name is required'); return; }
    setSaving(true);
    try {
      if (editingCatId) {
        await updateAddOnCategory(editingCatId, catFormData);
      } else {
        await addAddOnCategory(catFormData);
      }
      setIsCatAdding(false);
      setEditingCatId(null);
      setCatFormData({ name: '', description: '', selectionMode: 'single', required: false, sortOrder: 0, eventTypeIds: [], active: true });
    } catch (err) {
      alert(err.message || 'Failed to save category.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCat = async (id) => {
    if (confirm('Delete this category? Add-ons in it will become uncategorized.')) {
      try { await deleteAddOnCategory(id); } catch (err) { alert(err.message); }
    }
  };

  const handleCancelCat = () => {
    setIsCatAdding(false);
    setEditingCatId(null);
    setCatFormData({ name: '', description: '', selectionMode: 'single', required: false, sortOrder: 0, eventTypeIds: [], active: true });
  };

  const toggleEventType = (eventTypeId) => {
    setFormData(prev => ({
      ...prev,
      eventTypeIds: prev.eventTypeIds.includes(eventTypeId)
        ? prev.eventTypeIds.filter(id => id !== eventTypeId)
        : [...prev.eventTypeIds, eventTypeId]
    }));
  };

  const toggleCatEventType = (eventTypeId) => {
    setCatFormData(prev => ({
      ...prev,
      eventTypeIds: prev.eventTypeIds.includes(eventTypeId)
        ? prev.eventTypeIds.filter(id => id !== eventTypeId)
        : [...prev.eventTypeIds, eventTypeId]
    }));
  };

  const getEventTypeNames = (ids) => {
    if (!ids || ids.length === 0) return 'All Event Types';
    return ids.map(id => eventTypes.find(et => et.id === id)?.name).filter(Boolean).join(', ');
  };

  const getCategoryName = (catId) => {
    if (!catId) return 'Uncategorized';
    return addOnCategories.find(c => c.id === catId)?.name || 'Unknown';
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto pb-24">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Add-ons & Categories</h1>
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveSection('addons')}
          className={cn("flex-1 py-3 text-sm font-bold border-b-2 transition-colors",
            activeSection === 'addons' ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"
          )}
        >
          Add-ons ({addOns.length})
        </button>
        <button
          onClick={() => setActiveSection('categories')}
          className={cn("flex-1 py-3 text-sm font-bold border-b-2 transition-colors",
            activeSection === 'categories' ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"
          )}
        >
          Categories ({addOnCategories.length})
        </button>
      </div>

      {activeSection === 'addons' && (
        <>
          {!isAdding && (
            <div className="mb-4">
              <Button onClick={() => setIsAdding(true)}>
                <SafeIcon icon={FiIcons.FiPlus} className="mr-2" /> Add New Add-on
              </Button>
            </div>
          )}

          {isAdding && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">{editingId ? 'Edit Add-on' : 'New Add-on'}</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Name *</label>
                    <input type="text" className="w-full p-3 border border-gray-300 rounded-lg" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., 40-Person Setup" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
                    <select className="w-full p-3 border border-gray-300 rounded-lg" value={formData.categoryId}
                      onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}>
                      <option value="">None (standalone)</option>
                      {addOnCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Description *</label>
                  <textarea className="w-full p-3 border border-gray-300 rounded-lg h-20" value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Price ($)</label>
                    <input type="number" className="w-full p-3 border border-gray-300 rounded-lg" value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })} min="0" step="5" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Type</label>
                    <select className="w-full p-3 border border-gray-300 rounded-lg" value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                      <option value="flat">Flat Rate</option>
                      <option value="hourly">Per Hour</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Sort Order</label>
                    <input type="number" className="w-full p-3 border border-gray-300 rounded-lg" value={formData.sortOrder}
                      onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Image URL (optional)</label>
                  <input type="url" className="w-full p-3 border border-gray-300 rounded-lg" value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Available for Event Types <span className="text-xs font-normal text-gray-500">(Leave all unchecked for all)</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[...eventTypes].sort((a, b) => (b.active === a.active ? 0 : b.active ? 1 : -1)).map(et => (
                      <button key={et.id} type="button" onClick={() => toggleEventType(et.id)}
                        className={cn("flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-colors",
                          formData.eventTypeIds.includes(et.id) ? "border-primary bg-blue-50" : et.active ? "border-gray-200 bg-white hover:border-gray-300" : "border-dashed border-gray-300 bg-gray-50 hover:border-gray-400")}>
                        <div className={cn("w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                          formData.eventTypeIds.includes(et.id) ? "bg-primary border-primary" : "border-gray-400 bg-white")}>
                          {formData.eventTypeIds.includes(et.id) && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                        </div>
                        <p className="font-bold text-sm text-gray-900">
                          {et.name}
                          {!et.active && <span className="ml-1.5 text-xs font-medium text-gray-500">(Hidden)</span>}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <input type="checkbox" id="active-addon" className="w-4 h-4" checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />
                  <label htmlFor="active-addon" className="text-sm font-medium text-gray-700">Active</label>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={handleSave} disabled={saving}>{editingId ? 'Update' : 'Create'} Add-on</Button>
                <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {addOns.map((addon) => (
              <div key={addon.id} className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex gap-3 flex-1">
                    {addon.imageUrl && <img src={addon.imageUrl} alt={addon.name} className="w-12 h-12 rounded-lg object-cover" />}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900">{addon.name}</h3>
                        <span className={cn("text-xs px-2 py-0.5 rounded font-bold", addon.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500")}>
                          {addon.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{addon.description}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span>Price: <span className="text-primary font-bold">{formatCurrency(addon.price)}</span> {addon.type === 'hourly' ? '/hr' : 'flat'}</span>
                        <span>Category: <span className="font-bold text-gray-700">{getCategoryName(addon.categoryId)}</span></span>
                        <span>For: {getEventTypeNames(addon.eventTypeIds)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(addon)}><SafeIcon icon={FiIcons.FiEdit2} /></Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(addon.id)}><SafeIcon icon={FiIcons.FiTrash2} /></Button>
                  </div>
                </div>
              </div>
            ))}
            {addOns.length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                <SafeIcon icon={FiIcons.FiPackage} className="mx-auto text-4xl text-gray-300 mb-2" />
                <p className="text-gray-500">No add-ons created yet.</p>
              </div>
            )}
          </div>
        </>
      )}

      {activeSection === 'categories' && (
        <>
          {!isCatAdding && (
            <div className="mb-4">
              <Button onClick={() => setIsCatAdding(true)}>
                <SafeIcon icon={FiIcons.FiPlus} className="mr-2" /> Add Category
              </Button>
            </div>
          )}

          {isCatAdding && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">{editingCatId ? 'Edit Category' : 'New Category'}</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Name *</label>
                    <input type="text" className="w-full p-3 border border-gray-300 rounded-lg" value={catFormData.name}
                      onChange={(e) => setCatFormData({ ...catFormData, name: e.target.value })} placeholder="e.g., Setup Fee" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Selection Mode</label>
                    <select className="w-full p-3 border border-gray-300 rounded-lg" value={catFormData.selectionMode}
                      onChange={(e) => setCatFormData({ ...catFormData, selectionMode: e.target.value })}>
                      <option value="single">Single (mutually exclusive)</option>
                      <option value="multiple">Multiple (any combination)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
                  <input type="text" className="w-full p-3 border border-gray-300 rounded-lg" value={catFormData.description}
                    onChange={(e) => setCatFormData({ ...catFormData, description: e.target.value })} placeholder="Optional description" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Sort Order</label>
                    <input type="number" className="w-full p-3 border border-gray-300 rounded-lg" value={catFormData.sortOrder}
                      onChange={(e) => setCatFormData({ ...catFormData, sortOrder: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div className="flex items-end gap-4 pb-1">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="cat-required" className="w-4 h-4" checked={catFormData.required}
                        onChange={(e) => setCatFormData({ ...catFormData, required: e.target.checked })} />
                      <label htmlFor="cat-required" className="text-sm font-medium text-gray-700">Required</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="cat-active" className="w-4 h-4" checked={catFormData.active}
                        onChange={(e) => setCatFormData({ ...catFormData, active: e.target.checked })} />
                      <label htmlFor="cat-active" className="text-sm font-medium text-gray-700">Active</label>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Restrict to Event Types <span className="text-xs font-normal text-gray-500">(Leave all unchecked for all)</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[...eventTypes].sort((a, b) => (b.active === a.active ? 0 : b.active ? 1 : -1)).map(et => (
                      <button key={et.id} type="button" onClick={() => toggleCatEventType(et.id)}
                        className={cn("flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-colors",
                          catFormData.eventTypeIds.includes(et.id) ? "border-primary bg-blue-50" : et.active ? "border-gray-200 bg-white hover:border-gray-300" : "border-dashed border-gray-300 bg-gray-50 hover:border-gray-400")}>
                        <div className={cn("w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                          catFormData.eventTypeIds.includes(et.id) ? "bg-primary border-primary" : "border-gray-400 bg-white")}>
                          {catFormData.eventTypeIds.includes(et.id) && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                        </div>
                        <p className="font-bold text-sm text-gray-900">
                          {et.name}
                          {!et.active && <span className="ml-1.5 text-xs font-medium text-gray-500">(Hidden)</span>}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={handleSaveCat} disabled={saving}>{editingCatId ? 'Update' : 'Create'} Category</Button>
                <Button variant="outline" onClick={handleCancelCat}>Cancel</Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {addOnCategories.map(cat => {
              const catAddOns = addOns.filter(a => a.categoryId === cat.id);
              return (
                <div key={cat.id} className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900">{cat.name}</h3>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold">
                          {cat.selectionMode === 'single' ? 'Single Select' : 'Multi Select'}
                        </span>
                        {cat.required && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">Required</span>}
                        {!cat.active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-bold">Inactive</span>}
                      </div>
                      {cat.description && <p className="text-sm text-gray-600 mb-2">{cat.description}</p>}
                      <div className="text-xs text-gray-500">
                        <span>{catAddOns.length} add-on(s)</span>
                        <span className="mx-2">|</span>
                        <span>For: {getEventTypeNames(cat.eventTypeIds)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" variant="outline" onClick={() => handleEditCat(cat)}><SafeIcon icon={FiIcons.FiEdit2} /></Button>
                      <Button size="sm" variant="danger" onClick={() => handleDeleteCat(cat.id)}><SafeIcon icon={FiIcons.FiTrash2} /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
            {addOnCategories.length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                <SafeIcon icon={FiIcons.FiGrid} className="mx-auto text-4xl text-gray-300 mb-2" />
                <p className="text-gray-500">No categories created yet.</p>
                <p className="text-xs text-gray-400 mt-1">Categories group add-ons and enforce mutual exclusivity.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AddOnsManager;
